import { Ref, ref, watch, computed, reactive } from '@vue/composition-api';
import i18n, { i18n_t } from '@/locales/i18n';
import smh, { SMH } from '@/renderer/scripts/smh';
import tips from '@/renderer/scripts/utils/tips';

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export default (spaceId: Ref<SMH.SpaceId>, isPersonalSpaceType: Ref<boolean>) => {
  const searchKey = ref('');
  const searchKeyCache = ref('');
  // 是否处于搜索结果页
  const isSearching = ref(false);
  // 是否处于搜索loading态
  const isSearchLoading = ref(false);
  // 是否处于更多搜索的loading态
  const isLoadMoreLoading = ref(false);
  // 团队空间下，用户选择的检索类型
  const searchType: Ref<'dir' | 'team'> = ref('dir');
  const searchTypeCache: Ref<'dir' | 'team'> = ref('dir');
  const isSearchDirChoose = computed(() => searchType.value === 'dir');
  // 真实的检索类型，受用户选择的空间类型和检索类型影响
  const isSearchDirTruly = computed(() => isPersonalSpaceType.value || isSearchDirChoose.value);
  const isSearchDirCache = computed(() => searchTypeCache.value === 'dir');
  const sentencesAboutSearch = computed(() => {
    const spaceType = isPersonalSpaceType.value ? i18n_t('个人空间') : i18n_t('当前空间');
    const searchTypeMap = {
      team: i18n_t('部门'),
      dir: i18n_t('文件夹'),
    };
    let searchTypeStr = i18n_t('文件夹');
    if (!isPersonalSpaceType.value) {
      searchTypeStr = searchTypeMap[searchType.value] || searchTypeMap[searchTypeCache.value] || i18n_t('文件夹');
    }
    return {
      inputPlaceholder: i18n_t('搜索"{spaceType}"下的{searchTypeStr}', {
        spaceType,
        searchTypeStr,
      }),
      emptyTip: i18n_t('暂无与「{slot1}」相关的{searchTypeStr}', {
        slot1: searchKeyCache.value,
        searchTypeStr,
      }),
    };
  });
  const searchResponse: {
    contents: Object[];
    hasMore: boolean;
    nextMarker: number;
    limit: number;
    searchId: string;
    expire?: number;
  } = reactive({
    contents: [],
    hasMore: false,
    limit: 20,
    nextMarker: 0,
    searchId: '',
  });
  const methods = {
    onSearch: async (isloadMore?: boolean) => {
      searchKeyCache.value = searchKey.value;
      searchTypeCache.value = searchType.value;
      // 加载更多数据判断
      const isDirAgainSearch = isloadMore === true;
      const isTeamAgainSearch =
        isloadMore === true &&
        searchResponse.searchId &&
        searchResponse.expire &&
        Date.now() - searchResponse.expire < 60 * 60 * 1000;
      if (!searchKeyCache.value) {
        methods.onCancel();
        return;
      }
      isSearching.value = true;
      let res;
      try {
        if (isSearchDirTruly.value) {
          const params: SMH.CreateSearchTaskParams = {
            spaceId: spaceId.value,
            keyword: searchKey.value,
            limit: 20,
            type: ['dir'],
            scope: '/',
            accurate: false,
          };
          if (isloadMore === true && searchResponse.nextMarker) {
            isLoadMoreLoading.value = true;
            Object.assign(params, {
              marker: searchResponse.nextMarker,
            });
          } else {
            isSearchLoading.value = true;
            searchResponse.contents = [];
          }
          res = await smh.userAPI.createSearchTask(params);
        } else if (isTeamAgainSearch) {
          res = await smh.userAPI.listSearchTeamContent({
            searchId: searchResponse.searchId,
            marker: searchResponse.nextMarker,
          });
        } else {
          searchResponse.expire = Date.now();
          res = await smh.userAPI.createSearchTeamTask({
            keyword: searchKey.value,
            checkBelongingTeams: 1,
          });
        }
        const { data } = res;
        const { contents, hasMore, nextMarker, searchId } = data;
        isSearchLoading.value = false;
        isLoadMoreLoading.value = false;
        if ((isDirAgainSearch && isSearchDirTruly.value) || isTeamAgainSearch) {
          searchResponse.contents.push(...contents);
        } else {
          searchResponse.contents = contents;
        }
        searchResponse.hasMore = hasMore;
        searchResponse.nextMarker = nextMarker || 0;
        searchId && (searchResponse.searchId = searchId);
      } catch (error: MsgError) {
        isSearchLoading.value = false;
        isLoadMoreLoading.value = false;
        tips.error(error as MsgError);
      }
    },
    onCancel: () => {
      isSearching.value = false;
      searchResponse.contents = [];
      searchResponse.hasMore = false;
      searchResponse.nextMarker = 0;
      searchResponse.searchId = '';
    },
    onLoadMore: () => {
      methods.onSearch(true);
    },
  };
  watch(
    () => isSearching.value,
    value => {
      if (!value) {
        searchKey.value = '';
      }
    },
  );
  return {
    searchKey,
    searchType,
    isSearching,
    isSearchLoading,
    isLoadMoreLoading,
    isSearchDirChoose,
    isSearchDirCache,
    sentencesAboutSearch,
    searchResponse,
    methods,
  };
};
